import { usePageRefresh, usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useMemo, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTasksList, deleteTask, getTaskDashboard } from '../../../redux/actions/SocietyPortal/TaskAction/TaskAction';
import Pagination from '../../../components/common/Pagination';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import {
    TASK_STATUSES, TASK_PRIORITIES, TASK_DEPARTMENTS,
    statusBadgeClass, priorityBadgeClass, humanize,
} from '../../../consts/taskConstants';

const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (task) =>
    task.due_date && !['COMPLETED', 'CLOSED', 'CANCELLED'].includes(task.status) &&
    new Date(task.due_date).getTime() < Date.now();

const Tasks = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const {
        tasksListData, tasksListLoading, tasksPagination,
        deleteTaskLoading, taskDashboardData,
    } = useSelector((state) => state.TaskReducer);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [filterDepartment, setFilterDepartment] = useState('');
    const [filterDue, setFilterDue] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);

    const canDashboard = can(PERMISSIONS.TASK_DASHBOARD);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (canDashboard) dispatch(getTaskDashboard({}));
    }, [dispatch, canDashboard, refreshKey]);

    const fetchTasks = useMemo(() => () => {
        dispatch(getTasksList({
            page: currentPage,
            limit: itemsPerPage,
            search: searchQuery || undefined,
            status: filterStatus || undefined,
            priority: filterPriority || undefined,
            department: filterDepartment || undefined,
            due: filterDue || undefined,
            sort_by: 'created_at',
            sort_dir: 'desc',
        }));
    }, [dispatch, currentPage, itemsPerPage, searchQuery, filterStatus, filterPriority, filterDepartment, filterDue]);
    usePageRefresh(fetchTasks);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const tasks = Array.isArray(tasksListData) ? tasksListData : [];

    const stats = useMemo(() => {
        const d = taskDashboardData || {};
        return [
            { icon: 'hugeicons:task-01', color: 'tw-bg-blue-500', value: d.total ?? tasksPagination?.total ?? tasks.length, label: 'Total Tasks' },
            { icon: 'mdi:clock-outline', color: 'tw-bg-gray-500', value: d.pending ?? '—', label: 'Pending' },
            { icon: 'mdi:progress-clock', color: 'tw-bg-sky-500', value: d.in_progress ?? '—', label: 'In Progress' },
            { icon: 'mdi:alert-circle-outline', color: 'tw-bg-red-500', value: d.overdue ?? '—', label: 'Overdue' },
            { icon: 'mdi:calendar-today', color: 'tw-bg-amber-500', value: d.due_today ?? '—', label: 'Due Today' },
            { icon: 'mdi:check-circle-outline', color: 'tw-bg-emerald-500', value: d.completed ?? '—', label: 'Completed' },
        ];
    }, [taskDashboardData, tasksPagination, tasks.length]);

    const handleFilterChange = (setter, value) => {
        setter(value);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setFilterStatus('');
        setFilterPriority('');
        setFilterDepartment('');
        setFilterDue('');
        setCurrentPage(1);
    };

    const hasActiveFilters = searchQuery || filterStatus || filterPriority || filterDepartment || filterDue;

    const handleDeleteConfirm = () => {
        if (!taskToDelete) return;
        dispatch(deleteTask({
            taskId: taskToDelete.task_id,
            onSuccess: () => {
                setDeleteModalOpen(false);
                setTaskToDelete(null);
                fetchTasks();
                if (canDashboard) dispatch(getTaskDashboard({}));
            },
        }));
    };

    const totalPages = tasksPagination?.totalPages || 1;

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Tasks</h1>
                    <p className="tw-text-xs tw-text-gray-500">Track and manage society tasks</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={() => navigate('/society-portal/tasks/board')}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-border tw-border-gray-200 tw-text-gray-700 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:view-column-outline" /> Board
                    </button>
                    <button
                        onClick={() => navigate('/society-portal/tasks/projects')}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-border tw-border-gray-200 tw-text-gray-700 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-gray-50"
                    >
                        <Icon icon="hugeicons:folder-01" /> Projects
                    </button>
                    {can(PERMISSIONS.TASK_CREATE) && (
                        <button
                            onClick={() => navigate('/society-portal/tasks/add')}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                        >
                            <Icon icon="mdi:plus" /> New Task
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3 tw-mb-4">
                {stats.map((stat, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-lg tw-p-3 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5">
                        <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${stat.color} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                            <Icon icon={stat.icon} className="tw-text-white tw-text-lg" />
                        </div>
                        <div>
                            <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-none">{stat.value}</p>
                            <p className="tw-text-[11px] tw-text-gray-500">{stat.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        placeholder="Search by title, number or description..."
                        value={searchQuery}
                        onChange={(e) => handleFilterChange(setSearchQuery, e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                    />
                </div>
                <select value={filterStatus} onChange={(e) => handleFilterChange(setFilterStatus, e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Status</option>
                    {TASK_STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
                </select>
                <select value={filterPriority} onChange={(e) => handleFilterChange(setFilterPriority, e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Priority</option>
                    {TASK_PRIORITIES.map((p) => <option key={p} value={p}>{humanize(p)}</option>)}
                </select>
                <select value={filterDepartment} onChange={(e) => handleFilterChange(setFilterDepartment, e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Departments</option>
                    {TASK_DEPARTMENTS.map((d) => <option key={d} value={d}>{humanize(d)}</option>)}
                </select>
                <select value={filterDue} onChange={(e) => handleFilterChange(setFilterDue, e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">Any Due</option>
                    <option value="today">Due Today</option>
                    <option value="overdue">Overdue</option>
                </select>
                {hasActiveFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
            </div>

            {/* Loading */}
            {tasksListLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading tasks...</p>
                </div>
            )}

            {/* Table */}
            {!tasksListLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-sm">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Task</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Status</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Priority</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Assignee</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Due Date</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tasks.map((task) => (
                                <tr key={task.task_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-2">
                                        <button onClick={() => navigate(`/society-portal/tasks/view/${task.task_id}`)} className="tw-text-left">
                                            <p className="tw-font-medium tw-text-gray-800 tw-line-clamp-1 hover:tw-text-blue-600">{task.title}</p>
                                            <p className="tw-text-[10px] tw-text-gray-500">
                                                {task.task_number}
                                                {task.project?.name ? ` · ${task.project.name}` : ''}
                                            </p>
                                        </button>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${statusBadgeClass(task.status)}`}>
                                            {humanize(task.status)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${priorityBadgeClass(task.priority)}`}>
                                            {humanize(task.priority)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                        {task.assignee
                                            ? `${task.assignee.first_name || ''} ${task.assignee.last_name || ''}`.trim() || '—'
                                            : '—'}
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <span className={isOverdue(task) ? 'tw-text-red-600 tw-font-medium' : 'tw-text-gray-600'}>
                                            {formatDate(task.due_date)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                            <button onClick={() => navigate(`/society-portal/tasks/view/${task.task_id}`)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="View">
                                                <Icon icon="mdi:eye-outline" />
                                            </button>
                                            {can(PERMISSIONS.TASK_UPDATE) && (
                                                <button onClick={() => navigate(`/society-portal/tasks/edit/${task.task_id}`)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400" title="Edit">
                                                    <Icon icon="mdi:pencil-outline" />
                                                </button>
                                            )}
                                            {can(PERMISSIONS.TASK_DELETE) && (
                                                <button onClick={() => { setTaskToDelete(task); setDeleteModalOpen(true); }} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500" title="Delete">
                                                    <Icon icon="mdi:delete-outline" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {tasks.length === 0 && (
                        <div className="tw-text-center tw-py-10">
                            <Icon icon="hugeicons:task-01" className="tw-text-4xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No tasks found</p>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {!tasksListLoading && tasks.length > 0 && (
                <div className="tw-mt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={tasksPagination?.total || tasks.length}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(value) => { setItemsPerPage(value); setCurrentPage(1); }}
                        itemsPerPageOptions={[10, 20, 50, 100]}
                        itemLabel="tasks"
                    />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Delete Task</h3>
                                <p className="tw-text-sm tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
                            Are you sure you want to delete <span className="tw-font-semibold">{taskToDelete?.title}</span>?
                        </p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button onClick={() => { setDeleteModalOpen(false); setTaskToDelete(null); }} disabled={deleteTaskLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50">
                                Cancel
                            </button>
                            <button onClick={handleDeleteConfirm} disabled={deleteTaskLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50">
                                {deleteTaskLoading
                                    ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Deleting...</>
                                    : <><Icon icon="mdi:delete" /> Delete</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Tasks;
